import type { ColorScheme } from '../utils/colorScheme';
import type { MMECPalette } from '../contexts/MMECThemeContext';

function pick(cs: CSSStyleDeclaration, name: string): string {
  const v = cs.getPropertyValue(name).trim();
  return v || '';
}

/** True for #rgb / #rrggbb / rgb() light surfaces (e.g. CB void). */
function colorStringIsLight(cssColor: string): boolean {
  const s = cssColor.trim();
  const rgb = s.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (rgb) {
    const r = Number(rgb[1]);
    const g = Number(rgb[2]);
    const b = Number(rgb[3]);
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return lum > 0.62;
  }
  const hex6 = s.match(/^#([0-9a-f]{6})$/i);
  if (hex6) {
    const n = parseInt(hex6[1], 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    const lum = (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
    return lum > 0.62;
  }
  return false;
}

/**
 * Maps MMEC token values currently applied on `document.documentElement`
 * (via `data-palette` + mmec-tokens.css) into the React ColorScheme used by
 * `useTheme().colors`, so palette pills (HG / 15 / HK / CB) propagate through
 * components that read the theme object, not only CSS `var(--acc)`.
 */
export function readMmecTokensIntoColorScheme(base: ColorScheme, palette: MMECPalette): ColorScheme {
  if (typeof window === 'undefined') return base;

  const cs = getComputedStyle(document.documentElement);
  const voidC = pick(cs, '--void');
  const elev = pick(cs, '--elev');
  const elev2 = pick(cs, '--elev2');
  const hair = pick(cs, '--hair');
  const txt = pick(cs, '--txt');
  const txt2 = pick(cs, '--txt-2');
  const muted = pick(cs, '--muted');
  const acc = pick(cs, '--acc');
  const up = pick(cs, '--up');
  const down = pick(cs, '--down');
  const railAct = pick(cs, '--rail-act');

  if (!acc || !voidC) return base;

  const voidLight = palette === 'cb' || colorStringIsLight(voidC);
  const textInverse = voidLight ? '#ffffff' : base.textInverse;

  return {
    ...base,
    primary: acc,
    accent: acc,
    secondary: railAct || base.secondary,
    background: voidC,
    surface: elev || base.surface,
    surfaceHover: elev2 || base.surfaceHover,
    surfaceBorder: hair || base.surfaceBorder,
    text: txt || base.text,
    textSecondary: txt2 || base.textSecondary,
    textMuted: muted || base.textMuted,
    textInverse,
    success: up || base.success,
    error: down || base.error,
    info: acc || base.info,
    chartPrimary: acc,
    chartSecondary: railAct || base.chartSecondary,
    chartSuccess: up || base.chartSuccess,
    chartError: down || base.chartError,
    boughtCalls: up || base.boughtCalls,
    soldCalls: down || base.soldCalls,
    boughtPuts: down || base.boughtPuts,
    // D1: sold puts are bullish premium, so they read up (bias colour), like the tiles and reformed charts.
    soldPuts: up || base.soldPuts,
    sentimentPositive: up || base.sentimentPositive,
    sentimentNegative: down || base.sentimentNegative,
  };
}
